import React, { useState, useEffect, useMemo } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { checkInviteStatus, checkInviteStatusesBatch, checkInviteStatusesByUrls, getLatestPosts, isUsableProviderId, sendConnectionRequest, sendFirstLiMessage } from '../../redux/LIAuthSlice/liAuthSlice';

import { Linkedin, Send } from 'lucide-react';

import InboxModal from './InboxModal';
import EmailGenerationModal from '../NetworkingPage/NetContent/EmailGenerationModal';
import { toast } from 'react-toastify';

import GmailIcon from "../../assets/images/Other/gmail.png";
import { isUsablePhoto } from '../NetworkingPage/NetContent/NetPipelineRow';
import MessageIcon from "../../assets/images/Networking_Page/Message.png";

function capitalizeWords(str) {
    if (!str) return '';
    return str
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
}

const NetCard = ({ company, data, recruiterContacts, preferredContacts, relatedContacts, loading, handleCardClick }) => {
    const dispatch = useDispatch();

    const { user } = useSelector((state) => state.auth);
    const {
        inviteLoadingByContact,
        inviteStatusesByProviderId,
        inviteStatusesByContactId,
        inviteStatusesByPublicId,
        isLinkedInConnected,
    } = useSelector((state) => state.liauth);

    const publicIdFromUrl = (url) => {
        const s = String(url || '');
        const parts = s.split('/in/');
        if (parts.length < 2) return null;
        return parts[1].split('?')[0].replace(/\/$/, '').toLowerCase() || null;
    };

    const [selectedContact, setSelectedContact] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isInboxOpen, setIsInboxOpen] = useState(false);
    const [isEmailOpen, setIsEmailOpen] = useState(false);
    const [emailOutreachMode, setEmailOutreachMode] = useState('email');
    const [expandedTiers, setExpandedTiers] = useState({
        tier1: false,
        tier2: false,
        tier3: false,
    });

    useEffect(() => {
        if (isModalOpen && selectedContact?.providerId) {
            dispatch(getLatestPosts({
                providerId: selectedContact.providerId,
                accountId: selectedContact.accountId,
                limit: 3,
                profileUrl: selectedContact.linkedin_url
            }));
        }
    }, [isModalOpen, selectedContact, dispatch]);

    const allContacts = useMemo(() => company?.allContacts || [], [company?.allContacts]);

    const requestedStatusesRef = React.useRef(new Set());
    const requestedUrlStatusesRef = React.useRef(new Set());
    useEffect(() => {
        if (isLinkedInConnected?.accountId && allContacts.length > 0) {
            const needed = [];
            const neededByUrl = [];
            allContacts.forEach(contact => {
                const pid = contact.providerId;
                const cid = contact._id != null ? String(contact._id) : '';
                const hasUsablePid = isUsableProviderId(pid);

                if (hasUsablePid) {
                    // Re-check when unknown OR still 'pending' — a pending invite may
                    // have been accepted since (otherwise it never flips to Connected).
                    const st = inviteStatusesByProviderId[pid];
                    if ((st === undefined || st === 'pending') && !requestedStatusesRef.current.has(pid)) {
                        requestedStatusesRef.current.add(pid);
                        needed.push(pid);
                    }
                } else if (cid && contact.linkedin_url && /\/in\//.test(contact.linkedin_url)) {
                    // No usable provider id → resolve status from the profile URL,
                    // so invites sent directly on LinkedIn show up after refresh.
                    const st = inviteStatusesByContactId[cid];
                    if ((st === undefined || st === 'pending') && !requestedUrlStatusesRef.current.has(cid)) {
                        requestedUrlStatusesRef.current.add(cid);
                        neededByUrl.push({ contactId: cid, linkedinUrl: contact.linkedin_url });
                    }
                }
            });

            if (needed.length > 0) {
                // Batch request for efficiency (backend caps to 50).
                dispatch(checkInviteStatusesBatch({ accountId: isLinkedInConnected.accountId, providerIds: needed }));
            }
            if (neededByUrl.length > 0) {
                dispatch(checkInviteStatusesByUrls({ accountId: isLinkedInConnected.accountId, contacts: neededByUrl }));
            }
        }
    }, [dispatch, isLinkedInConnected?.accountId, allContacts, inviteStatusesByProviderId, inviteStatusesByContactId]);

    const toggleTier = (tierKey) => {
        setExpandedTiers((prev) => ({ ...prev, [tierKey]: !prev[tierKey] }));
    };

    const getVisibleContacts = (contacts, tierKey, limit = 5) => {
        if (expandedTiers[tierKey]) return contacts;
        return contacts?.slice(0, limit);
    };

    const testingFunction = () => {
        console.log("Company: ", recruiterContacts)
    }

    const getInitials = (name) => {
        if (!name) return '?';
        const parts = name.trim().split(' ').filter(Boolean);
        return parts.length >= 2
            ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
            : parts[0].slice(0, 2).toUpperCase();
    };

    const AVATAR_GRADIENTS = [
        ['#3F57E0', '#6377F0'], ['#7C3AED', '#A78BFA'], ['#059669', '#34D399'],
        ['#DC2626', '#F87171'], ['#B45309', '#FCD34D'], ['#0369A1', '#38BDF8'],
    ];
    const getAvatarGradient = (name) => {
        if (!name) return AVATAR_GRADIENTS[0];
        let h = 0;
        for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
        return AVATAR_GRADIENTS[Math.abs(h) % AVATAR_GRADIENTS.length];
    };

    const renderContactCard = (contact, idx, tierKey) => {
        const isSending = inviteLoadingByContact[contact?._id];
        const cid = contact?._id != null ? String(contact._id) : '';
        const pid = contact?.providerId != null ? String(contact.providerId) : '';
        // providerId is often non-unique (e.g. "ContactOut", "Cached") → don't use it for status.
        const providerStatus = isUsableProviderId(pid) ? inviteStatusesByProviderId[pid] : undefined;
        const contactStatus = cid ? (inviteStatusesByContactId[cid] || inviteStatusesByContactId[contact?._id]) : undefined;
        // DB-persisted status matched by the contact's LinkedIn public identifier
        // (survives logout, and covers scraped contacts without a provider id).
        const pubId = publicIdFromUrl(contact?.linkedin_url || contact?.profile_url);
        const publicStatus = pubId ? inviteStatusesByPublicId?.[pubId] : undefined;
        const currentInviteStatus = contactStatus || providerStatus || publicStatus;
        const hasEmail = Boolean(contact?.email && String(contact.email).trim());

        const normalizeLinkedinProfileUrl = (raw) => {
            const s = String(raw || '').trim();
            if (!s) return '';
            const noQuery = s.split('?')[0].replace(/\/$/, '');
            return noQuery.replace(/\/recent-activity\/all$/i, '');
        };

        // Full, openable LinkedIn profile URL for this contact (or '' if none).
        const liProfileUrl = (() => {
            const u = normalizeLinkedinProfileUrl(contact?.linkedin_url || contact?.profile_url);
            if (!u) return '';
            return /^https?:\/\//i.test(u) ? u : `https://${u.replace(/^\/+/, '')}`;
        })();
        const openLinkedinProfile = () => { if (liProfileUrl) window.open(liProfileUrl, '_blank', 'noopener,noreferrer'); };

        const handleSendInvite = async (profileUrl, accountId, contactId, providerId) => {
            console.log("Triggered");
            console.log("profileUrl:", profileUrl);
            console.log("accountId:", accountId);
            console.log("contactId:", contactId);
            console.log("providerId:", providerId);

            const normalized = normalizeLinkedinProfileUrl(profileUrl);
            if (!accountId || !contactId || !normalized) {
                toast.error('Missing LinkedIn profile URL. Open profile manually.');
                return;
            }

            try {
                await dispatch(sendConnectionRequest({
                    profileUrl: normalized,
                    accountId,
                    contactId,
                    providerId
                })).unwrap();
                toast.success('Invite sent. Status: Pending.');
            } catch (err) {
                toast.error(typeof err === 'string' ? err : 'Failed to send invite.');
            }
        };

        const renderInviteButton = () => {
            // 1. When the API call is in progress
            if (isSending) {
                return (
                    <button
                        disabled
                        className='inline-flex items-center gap-1.5 h-9 px-4 rounded-full border border-gray-200 bg-gray-50 text-gray-400 font-semibold text-[12px] cursor-wait whitespace-nowrap'
                    >
                        Sending...
                    </button>
                );
            }

            // 2. Check the persistent status from LinkedIn
            switch (currentInviteStatus) {
                case 'accepted':
                    return (
                        <span
                            className='inline-flex items-center gap-1.5 h-9 px-4 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 font-semibold text-[12px] whitespace-nowrap'
                        >
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>
                            Connected
                        </span>
                    );
                case 'pending':
                    return (
                        <div className="relative group inline-flex">
                            <button
                                disabled
                                className='inline-flex items-center gap-1.5 h-9 px-4 rounded-full border border-amber-300 bg-amber-50 text-amber-700 font-semibold text-[12px] cursor-not-allowed whitespace-nowrap'
                            >
                                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                                Invite sent
                            </button>
                            {/* Hover tooltip */}
                            <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 rounded-lg bg-[#0F172A] text-white text-[11px] font-medium leading-snug whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-20 shadow-lg">
                                Invitation sent — waiting for them to accept
                                <span className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-[5px] border-transparent border-t-[#0F172A]" />
                            </span>
                        </div>
                    );
                case 'failed':
                case 'error':
                    return isLinkedInConnected?.accountId ? (
                        <button
                            className="inline-flex items-center h-9 px-4 rounded-full border border-red-300 bg-red-50 text-red-600 font-semibold text-[12px] hover:bg-red-100 transition-colors whitespace-nowrap"
                            onClick={() => handleSendInvite(contact.linkedin_url, isLinkedInConnected.accountId, contact._id, contact.providerId)}
                        >
                            Retry
                        </button>
                    ) : (
                        <button disabled className="inline-flex items-center h-9 px-4 rounded-full border border-gray-200 text-gray-400 font-semibold text-[12px] cursor-not-allowed whitespace-nowrap">
                            Connect LinkedIn
                        </button>
                    );
                default:
                    return isLinkedInConnected?.accountId ? (
                        <button
                            className="inline-flex items-center gap-1.5 justify-center px-3.5 py-2 rounded-full bg-[#3F57E0] hover:bg-[#3346B8] text-white font-bold text-[12.5px] transition-colors whitespace-nowrap"
                            style={{ boxShadow: '0 4px 10px rgba(63,87,224,.3)' }}
                            onClick={() => handleSendInvite(contact.linkedin_url, isLinkedInConnected.accountId, contact._id, contact.providerId)}
                        >
                            <Send size={12} />
                            Send Invite
                        </button>
                    ) : (
                        <button disabled className="inline-flex items-center justify-center px-3.5 py-2 rounded-full bg-gray-100 text-gray-400 font-bold text-[12.5px] cursor-not-allowed whitespace-nowrap">
                            Connect LinkedIn
                        </button>
                    );

            }
        };

        const tier = TIER_META[tierKey] || TIER_META.tier1;
        const [g1, g2] = getAvatarGradient(contact.name);
        const photo = contact.profile_picture || contact.photo_url || contact.image;
        const hasRealPhoto = isUsablePhoto(photo);
        const mutuals = contact.mutual_connections || contact.mutualConnections || null;

        return (
            <div key={idx}
                className='w-full bg-white rounded-[14px] border border-[#E6E8EE] hover:shadow-lg transition-all'
                style={{ boxShadow: '0 2px 8px rgba(15,23,42,0.05)' }}
            >
                <div className='flex items-center gap-3.5 px-4 py-3.5'>
                    {/* Avatar with LinkedIn badge — initials base layer; photo overlaid
                        on top so a broken/expired/ghost photo reveals the initials. */}
                    <div className="relative shrink-0 w-11 h-11">
                        <div
                            className="w-11 h-11 rounded-full flex items-center justify-center text-white font-bold text-[15px] select-none"
                            style={{ background: `linear-gradient(135deg, ${g1}, #6377F0)`, boxShadow: 'inset 0 -1px 2px rgba(0,0,0,.15)' }}
                        >
                            {getInitials(contact.name)}
                        </div>
                        {hasRealPhoto && (
                            <img
                                src={photo}
                                alt={contact.name}
                                className="absolute inset-0 w-11 h-11 rounded-full object-cover"
                                onError={(e) => { e.target.style.display = 'none'; }}
                            />
                        )}
                        {contact.linkedin_url && (
                            <a href={contact.linkedin_url} target="_blank" rel="noopener noreferrer"
                                className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-white rounded-full flex items-center justify-center border-2 border-white" style={{ boxShadow: '0 1px 2px rgba(0,0,0,.2)' }}>
                                <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="#0A66C2">
                                    <path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z"/><circle cx="4" cy="4" r="2"/>
                                </svg>
                            </a>
                        )}
                    </div>

                    {/* Info — clicking navigates to the contact's LinkedIn profile. */}
                    <div
                        role={liProfileUrl ? 'link' : undefined}
                        title={liProfileUrl ? 'View LinkedIn profile' : undefined}
                        onClick={openLinkedinProfile}
                        className={`flex flex-col min-w-0 flex-1 ${liProfileUrl ? 'cursor-pointer group' : ''}`}
                    >
                        <p className={`text-[14px] font-bold text-[#0F172A] leading-tight text-left ${liProfileUrl ? 'group-hover:text-[#3F57E0] group-hover:underline' : ''}`}>{contact.name}</p>
                        <p className='text-[11.5px] font-medium text-[#334155] mt-0.5 leading-snug text-left'>{contact.title}</p>
                        <div className='flex items-center justify-start gap-1.5 mt-1 flex-wrap'>
                            <svg className="w-[11px] h-[11px] text-[#94A3B8] shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/></svg>
                            <span className='text-[11px] text-[#64748B] text-left'>{data?.companyName}</span>
                            {mutuals != null && (
                                <>
                                    <span className="text-[#CBD5E1]">·</span>
                                    <span className='text-[11px] font-semibold' style={{ color: tier.color }}>{mutuals} mutual</span>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Actions */}
                    <div className='flex items-center gap-2 shrink-0'>
                        {renderInviteButton()}
                        {currentInviteStatus === 'accepted' && isLinkedInConnected?.accountId && (
                            <button
                                onClick={() => {
                                    setEmailOutreachMode('message');
                                    setSelectedContact({ ...contact, _tierLabel: tier.label });
                                    setIsEmailOpen(true);
                                }}
                                title="Message on LinkedIn"
                                className="h-[38px] px-2.5 inline-flex items-center gap-1.5 rounded-[10px] border border-[#E6E8EE] bg-white hover:border-[#0A66C2]/40 hover:bg-[#0A66C2]/5 transition-colors"
                            >
                                {/* Send/DM glyph + LinkedIn "in" logo, merged */}
                                <Send className="w-[15px] h-[15px]" style={{ color: '#0A66C2' }} strokeWidth={2.2} />
                                <Linkedin className="w-[17px] h-[17px]" style={{ color: '#0A66C2' }} fill="#0A66C2" strokeWidth={0} />
                            </button>
                        )}
                        <button
                            onClick={() => { if (!hasEmail) return; setEmailOutreachMode('email'); setIsEmailOpen(true); setSelectedContact({ ...contact, _tierLabel: tier.label }); }}
                            disabled={!hasEmail}
                            title={hasEmail ? 'Send Email' : 'No email available'}
                            className={`w-[38px] h-[38px] inline-flex items-center justify-center rounded-[10px] border transition-colors bg-white ${
                                hasEmail ? 'border-[#E6E8EE] hover:border-red-200 hover:bg-red-50' : 'border-gray-100 cursor-not-allowed opacity-50'
                            }`}
                        >
                            <img className='w-[18px] h-[18px]' src={GmailIcon} alt="Email" />
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const TIER_META = {
        tier1: { label: 'Tier 1', sub: 'Recruiters & Talent Acquisition', color: '#3F57E0', bg: '#EEF1FE', accent: '#3F57E0' },
        tier2: { label: 'Tier 2', sub: 'Your Preferred Department',       color: '#059669', bg: '#ECFDF5', accent: '#059669' },
        tier3: { label: 'Tier 3', sub: 'Other Company Contacts',          color: '#B45309', bg: '#FEF3C7', accent: '#F59E0B' },
    };

    const renderTierColumn = (title, contacts, tierKey) => {
        const meta = TIER_META[tierKey];
        return (
            <div className="flex flex-col gap-3">
                {/* Tier header */}
                <div className="flex flex-col gap-0.5">
                    <div className="flex items-center gap-2">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-[11px] font-bold" style={{ background: meta.bg, color: meta.color }}>
                            {meta.label}
                        </span>
                        <span className="text-[11px] font-semibold text-[#64748B]">{contacts?.length || 0} contacts</span>
                    </div>
                    <p className="text-[10.5px] text-[#94A3B8]">{meta.sub}</p>
                </div>
                <div className="space-y-2.5">
                    {contacts?.length > 0 ? (
                        getVisibleContacts(contacts, tierKey).map((c, i) => renderContactCard(c, i, tierKey))
                    ) : (
                        <div className="flex flex-col items-center justify-center text-center h-[170px] rounded-xl border border-dashed border-[#D6DAE3] bg-[#FAFBFD]">
                            <svg className="w-6 h-6 text-[#CBD2DD]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8"><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                            <p className="text-[13px] text-[#94A3B8] mt-2.5 font-medium">No contacts in this tier</p>
                        </div>
                    )}
                </div>
                {contacts?.length > 5 && (
                    <button onClick={() => toggleTier(tierKey)} className="text-[12px] font-semibold text-[#3F57E0] hover:underline">
                        {expandedTiers[tierKey] ? 'View less' : `View all ${contacts.length}`}
                    </button>
                )}
            </div>
        );
    };

    return (
        <>
            <div className="pt-5">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    {renderTierColumn('Tier 1', recruiterContacts, 'tier1')}
                    {renderTierColumn('Tier 2', preferredContacts, 'tier2')}
                    {renderTierColumn('Tier 3', relatedContacts, 'tier3')}
                </div>
            </div>

            <InboxModal
                isOpen={isInboxOpen}
                onClose={() => {
                    setIsInboxOpen(false);
                    setSelectedContact(null);
                }}
                initialSelectedContact={selectedContact}
            />

            <EmailGenerationModal
                isOpen={isEmailOpen}
                onClose={() => {
                    setIsEmailOpen(false);
                    setSelectedContact(null);
                    setEmailOutreachMode('email');
                }}
                contact={selectedContact}
                taskId={selectedContact ? `mynet-${selectedContact._id || selectedContact.contactid || 'contact'}` : undefined}
                inviteStatus={emailOutreachMode === 'message' ? 'accepted' : undefined}
                jobData={{
                    jobPosition: data?.jobTitle || data?.jobPosition || 'opportunities',
                    jobDescription: data?.jobDescription || data?.description || ''
                }}
                companyData={{
                    name: data?.companyName || selectedContact?.organization_name || '',
                    companyLogo: data?.companyLogo || ''
                }}
                outreachMode={emailOutreachMode}
            />
        </>
    );
};

export default NetCard;